import { customElement, state } from 'lit/decorators.js';
import { html } from 'lit';
import { chatbotMessageRatingStyles } from './ChatbotMessageRating.styles';
import { ChatbotService, type RateConversationMessageRequestModel } from '@atlas/shared/data-access/chatbot';
import '@atlas/shared/client/ui/wc/icons/IconThumbDown';
import '@atlas/shared/client/ui/wc/icons/IconThumbUp';
import { type IconButton } from '@atlas/shared/client/ui/wc/components/icon-button';
import { sleep } from '@atlas/shared/client/ui/core/utils';
import { consume } from '@lit/context';
import { chatbotMessageContext, type ChatbotMessageContext } from '../chatbot-message/ChatbotMessageContext';
import { SuperElement } from '@atlas/shared/client/ui/wc/internals/super/SuperElement';

@customElement('lr-chatbot-message-rating')
export class ChatbotMessageRating extends SuperElement {
    @consume({ context: chatbotMessageContext })
    messageContext?: ChatbotMessageContext;

    @state() private upActive = false;
    @state() private downActive = false;
    @state() private score?: number = this.messageContext?.score;

    static styles = [SuperElement.styles, chatbotMessageRatingStyles];
    async toggleButton(e: Event, score: number) {
        const iconButton = e.target as IconButton;
        iconButton.loading = true;
        try {
            await this.evaluateResponse(score);
            this.score = score;
            if (score === 1) {
                this.upActive = !this.upActive;
                this.downActive = false;
            } else if (score === 0) {
                this.downActive = !this.downActive;
                this.upActive = false;
            } else {
                this.upActive = !this.upActive;
                this.downActive = false;
            }
        } catch {
        } finally {
            iconButton.loading = false;
        }
    }

    private async evaluateResponse(score: number) {
        if (!this.messageContext?.apiUrl || !this.messageContext?.conversationId || !this.messageContext?.messageId) {
            throw Error('Missing information to call API on message rating');
        }
        const service = new ChatbotService({
            baseURL: this.messageContext.apiUrl,
            withCredentials: true,
            method: 'POST'
        });
        const postData: RateConversationMessageRequestModel = {
            score: score,
            feedback: ''
        };
        await service.chatbotConversationsMessagesRatePost(
            { id: this.messageContext.conversationId, messageId: this.messageContext.messageId },
            postData
        );
        // Fake latency for better UX
        await sleep(500);
    }

    renderElement() {
        return html`
            <lr-icon-button
                size="small"
                variant="tertiary-transparent"
                icon="thumb-up"
                ?data-toggled="${this.score === 1}"
                @click=${(e: Event) => this.toggleButton(e, 1)}
            >
            </lr-icon-button>
            <lr-icon-button
                size="small"
                variant="tertiary-transparent"
                icon="thumb-down"
                ?data-toggled="${this.score === 0}"
                @click=${(e: Event) => this.toggleButton(e, 0)}
            >
            </lr-icon-button>
        `;
    }
}
